// ============================================================================
// Separations — the index, and everybody's own list.
//
// The list of RECORDS is visible to HR, the executives and Finance, because
// that is who employee_separations RLS returns rows to. Underneath it, "Items
// assigned to me" is visible to ANYONE holding one — which is the door a
// Supervisor or a National AM comes in through. Neither section is gated in
// this file: both render whatever the database returned, which is the only
// gate that can be trusted.
// ============================================================================

import { canWriteHrRecord } from '../../shared/role-gates';
import { Link } from 'react-router-dom';
import { AlertTriangle, Plus } from 'lucide-react';
import {
  Button,
  Card,
  ErrorState,
  PageHeader,
  Skeleton,
} from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { useMyOpenItems, useSeparationList } from './hooks/use-separation-data';
import { OWNER_LABELS, reasonLabel } from './types';


export default function SeparationList() {
  const { profile } = useAuth();
  const { rows, loading, error, refetch } = useSeparationList();
  const { items: myItems, loading: myLoading } = useMyOpenItems();
  const canStart = canWriteHrRecord(profile);

  if (error) return <ErrorState title="Couldn't load separations" body={error} />;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <PageHeader
        eyebrow="People"
        title="Separations"
        meta={
          loading
            ? 'Loading…'
            : `${rows.filter((r) => r.status === 'In Progress').length} in progress · ${rows.length} recorded`
        }
        actions={
          <>
            <Button variant="ghost" size="sm" as={Link} to="/people/separations/loose-ends">
              Loose Ends report
            </Button>
            {canStart && (
              <Button size="sm" icon={<Plus className="h-4 w-4" />} as={Link} to="/people/separations/new">
                Record a separation
              </Button>
            )}
          </>
        }
      />

      <section>
        <h2 className="mb-2 text-sm font-semibold text-tcs-text">Items assigned to me</h2>
        {myLoading ? (
          <Skeleton className="h-16" />
        ) : myItems.length === 0 ? (
          <Card className="p-4 text-sm text-tcs-text-secondary">
            Nothing is waiting on you.
          </Card>
        ) : (
          <div className="space-y-2">
            {myItems.map((i) => (
              <Card key={i.item_id} className="flex items-center gap-3 p-3">
                {i.is_blocking && (
                  <AlertTriangle className="h-4 w-4 shrink-0 text-sentiment-critical-text" />
                )}
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/people/separations/${i.separation_id}`}
                    className="text-sm font-medium text-tcs-text hover:underline"
                  >
                    {i.label}
                  </Link>
                  <div className="text-xs text-tcs-text-muted">
                    {i.separation_id} ·{' '}
                    {OWNER_LABELS[i.owner_position_key] ?? i.owner_position_key}
                    {i.due_date ? ` · due ${i.due_date}` : ''}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-tcs-text">Separation records</h2>
        {loading ? (
          <Skeleton className="h-32" />
        ) : rows.length === 0 ? (
          <Card className="p-4 text-sm text-tcs-text-secondary">
            No separation records are visible to you. The record itself is readable by HR,
            the executives and Finance.
          </Card>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <Card key={r.separation_id} className="p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <Link
                      to={`/people/separations/${r.separation_id}`}
                      className="text-sm font-medium text-tcs-text hover:underline"
                    >
                      {r.separation_id} · {r.employee_name ?? r.employee_id}
                    </Link>
                    <div className="text-xs text-tcs-text-muted">
                      {reasonLabel(r.separation_type)}
                      {r.last_working_day ? ` · last day ${r.last_working_day}` : ''}
                      {r.source === 'backfill' ? ' · recorded retrospectively' : ''}
                    </div>
                  </div>
                  <div className="text-xs text-tcs-text-secondary">
                    {r.status === 'Completed' ? (
                      'Closed'
                    ) : (
                      <>
                        {r.total_items - r.open_items} of {r.total_items} done
                        {r.blocking_items > 0 && (
                          <span className="ml-2 text-sentiment-critical-text">
                            {r.blocking_items} blocking
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
        <button type="button" onClick={refetch} className="sr-only">
          Refresh
        </button>
      </section>
    </div>
  );
}
